import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FaUsersCog, FaSwimmer, FaCalendarAlt, FaMoneyCheckAlt, FaComments,
  FaRegCalendarCheck, FaFileAlt, FaBullhorn, FaUniversity, FaShoppingCart,
  FaPlayCircle, FaQuestionCircle, FaUsers, FaRegNewspaper, FaBookOpen, FaPhone,
  FaHouseUser, FaLock, FaBars, FaTimes
} from 'react-icons/fa';

const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [mobileDropdown, setMobileDropdown] = useState(null);

  const mobileToggle = (label) => {
    setMobileDropdown((prev) => (prev === label ? null : label));
  };

  const featuresItems = [
    { icon: <FaUsersCog />, label: "HOA Management" },
    { icon: <FaSwimmer />, label: "Amenities" },
    { icon: <FaCalendarAlt />, label: "Reservations" },
    { icon: <FaMoneyCheckAlt />, label: "Payments" },
    { icon: <FaComments />, label: "Communications Center" },
    { icon: <FaRegCalendarCheck />, label: "Events" },
    { icon: <FaFileAlt />, label: "Documentation" },
    { icon: <FaBullhorn />, label: "Announcements" },
    { icon: <FaUniversity />, label: "Public Services" },
    { icon: <FaShoppingCart />, label: "Conveniences" },
  ];

  const resourcesItems = [
    { icon: <FaPlayCircle />, label: "Demo" },
    { icon: <FaQuestionCircle />, label: "Why Community Portal?", link: "/why-community-portal" },
    { icon: <FaUsers />, label: "About Us", link: "/about-us" },
    { icon: <FaRegNewspaper />, label: "Blog Posts" },
    { icon: <FaHouseUser />, label: "HOA Portal", external: "https://hoaportal.io" },
    { icon: <FaLock />, label: "Privacy", link: "/privacy" },
  ];

  const supportItems = [
    { icon: <FaBookOpen />, label: "Help Articles" },
    { icon: <FaQuestionCircle />, label: "FAQ", link: "/faq" },
    { icon: <FaPhone />, label: "Contact Us", link: "/contact-us" },
  ];

  return (
    <>
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link
            to="/"
            className="text-4xl font-extrabold bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 bg-clip-text text-transparent"
          >
            CommunityPortal
          </Link>

          <nav className="hidden md:flex items-center space-x-6 text-sm font-medium text-gray-700">
            <a href="/" className="hover:text-indigo-600">Home</a>
            <Dropdown label="Features" items={featuresItems} />
            <a href="#how-it-works" className="hover:text-indigo-600">How it Works</a>
            <Dropdown label="Resources" items={resourcesItems} />
            <Dropdown label="Support" items={supportItems} />
            <a
              href="https://app.communityportal.ai/login"
              className="px-4 py-2 border border-indigo-600 text-indigo-600 rounded-md hover:bg-indigo-50"
            >
              Login
            </a>

            <a
              href="https://app.communityportal.ai/get-started"
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
            >
              Get Started
            </a>
            
          </nav>

          <div className="md:hidden">
            <button onClick={() => setIsMobileMenuOpen(true)} className="text-gray-700 text-2xl" aria-label="Open Menu">
              <FaBars />
            </button>
          </div>
        </div>
      </header>

      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-50 backdrop-blur-sm bg-white/90 flex flex-col px-6 py-8 space-y-6 transition-all duration-300 overflow-y-auto">
          <button
            onClick={() => {
              setIsMobileMenuOpen(false);
              setMobileDropdown(null);
            }}
            className="text-gray-700 text-2xl self-end"
            aria-label="Close Menu"
          >
            <FaTimes />
          </button>

          <nav className="flex flex-col space-y-5 text-lg font-medium text-gray-800">
            <a href="/" onClick={() => setIsMobileMenuOpen(false)}>Home</a>

            <button onClick={() => mobileToggle('features')} className="flex items-center justify-between">
              <span className="text-left">Features</span>
            </button>
            {mobileDropdown === 'features' && (
              <div className="ml-4 space-y-3">
                {featuresItems.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm text-gray-700">
                    <span className="text-indigo-500">{item.icon}</span>
                    <span>{item.label}</span>
                  </div>
                ))}
              </div>
            )}

            <a href="#how-it-works" onClick={() => setIsMobileMenuOpen(false)}>How It Works</a>

            <button onClick={() => mobileToggle('resources')} className="flex items-center justify-between">
              <span className="text-left">Resources</span>
            </button>
            {mobileDropdown === 'resources' && (
              <div className="ml-4 space-y-3">
                {resourcesItems.map((item, idx) => (
                  item.external ? (
                    <a
                      href={item.external}
                      target="_blank"
                      rel="noopener noreferrer"
                      key={idx}
                      className="flex items-center gap-2 text-sm text-gray-700"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <span className="text-indigo-500">{item.icon}</span>
                      <span>{item.label}</span>
                    </a>
                  ) : item.link ? (
                    <Link
                      to={item.link}
                      key={idx}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="flex items-center gap-2 text-sm text-gray-700"
                    >
                      <span className="text-indigo-500">{item.icon}</span>
                      <span>{item.label}</span>
                    </Link>
                  ) : (
                    <div key={idx} className="flex items-center gap-2 text-sm text-gray-700">
                      <span className="text-indigo-500">{item.icon}</span>
                      <span>{item.label}</span>
                    </div>
                  )
                ))}
              </div>
            )}

            <button onClick={() => mobileToggle('support')} className="flex items-center justify-between">
              <span className="text-left">Support</span>
            </button>
            {mobileDropdown === 'support' && (
              <div className="ml-4 space-y-3">
                {supportItems.map((item, idx) => (
                  item.external ? (
                    <a
                      key={idx}
                      href={item.external}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-sm text-gray-700"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <span className="text-indigo-500">{item.icon}</span>
                      <span>{item.label}</span>
                    </a>
                  ) : item.link ? (
                    <Link
                      key={idx}
                      to={item.link}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="flex items-center gap-2 text-sm text-gray-700"
                    >
                      <span className="text-indigo-500">{item.icon}</span>
                      <span>{item.label}</span>
                    </Link>
                  ) : (
                    <div key={idx} className="flex items-center gap-2 text-sm text-gray-700">
                      <span className="text-indigo-500">{item.icon}</span>
                      <span>{item.label}</span>
                    </div>
                  )
                ))}

              </div>
            )}
            <a
              href="https://app.communityportal.ai/login"
              className="px-4 py-2 border border-indigo-600 text-indigo-600 rounded-md hover:bg-indigo-50 text-center"
            >
              Login
            </a>

            <a
              href="https://app.communityportal.ai/get-started"
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-center"
            >
              Get Started
            </a>
          </nav>
        </div>
      )}
    </>
  );
};

export default Navbar;

const Dropdown = ({ label, items }) => (
  <div className="relative group">
    <button className="hover:text-indigo-600">{label}</button>
    <div className="absolute left-0 mt-2 w-[320px] bg-white shadow-xl rounded-md opacity-0 group-hover:opacity-100 invisible group-hover:visible transition-all duration-200 z-50 p-4 space-y-3">
      {items.map((item, idx) => (
        item.external ? (
          <a
            href={item.external}
            target="_blank"
            rel="noopener noreferrer"
            key={idx}
            className="flex items-center gap-3 text-gray-700 hover:text-indigo-600"
          >
            <div className="text-indigo-500">{item.icon}</div>
            <span className="text-sm font-medium">{item.label}</span>
          </a>
        ) : item.link ? (
          <Link
            to={item.link}
            key={idx}
            className="flex items-center gap-3 text-gray-700 hover:text-indigo-600"
          >
            <div className="text-indigo-500">{item.icon}</div>
            <span className="text-sm font-medium">{item.label}</span>
          </Link>
        ) : (
          <div
            key={idx}
            className="flex items-center gap-3 text-gray-700 hover:text-indigo-600 cursor-pointer"
          >
            <div className="text-indigo-500">{item.icon}</div>
            <span className="text-sm font-medium">{item.label}</span>
          </div>
        )
      ))}
    </div>
  </div>
);
